import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Flex,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  VStack,
  HStack,
  Checkbox,
  Button,
  useToast,
  Divider,
  Link,
  Image,
  Badge,
} from "@chakra-ui/react";
import { FaFileAlt, FaImage, FaVideo, FaFilePdf, FaDownload } from "react-icons/fa";
import axios from "../../axiosConfig";
import parse from "html-react-parser";
import WidgetStep from "./WidgetStep";
import { getOrganizationSurveyId } from "../../utils/organizationSurveyHelper";

const fileIconFor = (type) => {
  if (!type) return <FaFileAlt />;
  if (type.startsWith("image")) return <FaImage />;
  if (type.startsWith("video")) return <FaVideo />;
  if (type === "application/pdf") return <FaFilePdf />;
  return <FaFileAlt />;
};

const DEFAULT_ATTACHMENTS = [];

const hasMeaningfulContent = (value) => {
  if (!value) return false;

  return value
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim()
    .length > 0;
};

// ✅ Robust S3 key extraction (no URL normalization bugs)
const extractS3Key = (url) => {
  if (!url) return null;

  // remove querystring if any
  const clean = url.split("?")[0];

  // Most common form: https://bucket.s3.region.amazonaws.com/<key>
  const marker = ".amazonaws.com/";
  const idx = clean.indexOf(marker);

  let key;
  if (idx !== -1) {
    key = clean.substring(idx + marker.length);
  } else {
    // Fallback: take the path part
    try {
      key = new URL(clean).pathname.replace(/^\//, "");
    } catch {
      // last resort: assume it's already a key
      key = clean.replace(/^\//, "");
    }
  }

  // Critical: convert "%20" -> " " etc.
  return decodeURIComponent(key);
};

const Widget = ({
  widgetId,
  organizationSurveyId = null,
  widget: preloadedWidget = null,
  attachments: preloadedAttachments = DEFAULT_ATTACHMENTS,
  mode = "legacy",
}) => {
  const isWorkbookMode = mode === "workbook";
  const [widget, setWidget] = useState(preloadedWidget);
  const [loading, setLoading] = useState(!isWorkbookMode);
  const [widgetError, setWidgetError] = useState(null);

  const [checklistItems, setChecklistItems] = useState([]);
  const [progress, setProgress] = useState({});

  // ✅ RESOURCES STATE
  const [resourceAttachments, setResourceAttachments] = useState(preloadedAttachments);
  const [activeAttachment, setActiveAttachment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const DEFAULT_REFLECTION_TITLE = "Reflection";
  const orgSurveyId = getOrganizationSurveyId(null, {
    organization_survey_id: organizationSurveyId,
  });

  const toast = useToast();

  // ✅ Load widget
  useEffect(() => {
    if (isWorkbookMode) return undefined;

    const fetchWidget = async () => {
      try {
        const params = orgSurveyId != null
          ? { organization_survey_id: orgSurveyId }
          : undefined;
        const res = await axios.get(`/widgets/${widgetId}`, { params });
        setWidget(res.data);
        setWidgetError(null);
      } catch (err) {
        const status = err?.response?.status;
        const apiMessage = err?.response?.data?.error || err?.response?.data?.message;
        setWidget(null);
        setWidgetError({ status, message: apiMessage });
        console.error("Failed to load widget", err);
      } finally {
        setLoading(false);
      }
    };
    fetchWidget();
  }, [
    isWorkbookMode,
    orgSurveyId,
    organizationSurveyId,
    widgetId,
  ]);

  useEffect(() => {
    if (!isWorkbookMode) return;
    if (!preloadedWidget) return;

    console.log("[Widget] Workbook mode init:", preloadedWidget?.id);

    const workbookAttachments =
      preloadedAttachments?.length > 0
        ? preloadedAttachments
        : preloadedWidget?.attachments ||
          preloadedWidget?.widget_attachments ||
          [];

    if (widget?.id !== preloadedWidget?.id) {
      setWidget(preloadedWidget);
    }
    setResourceAttachments(workbookAttachments);
    setChecklistItems([]);
    setProgress({});
    setWidgetError(null);
    setLoading(false);
  }, [isWorkbookMode, preloadedAttachments, preloadedWidget, widget?.id]);

  // ✅ Load checklist
  useEffect(() => {
    if (isWorkbookMode) {
      setChecklistItems([]);
      return undefined;
    }

    const fetchChecklistItems = async () => {
      if (!widgetId) return;
      if (!orgSurveyId) {
        setChecklistItems([]);
        console.warn(
          "Skipping widget checklist_items fetch: missing organization_survey_id",
          { widgetId }
        );
        return;
      }

      try {
        const response = await axios.get(`/widgets/${widgetId}/checklist_items`, {
          params: { organization_survey_id: orgSurveyId },
        });
        setChecklistItems(response.data || []);
      } catch (error) {
        console.error("Error fetching widget checklist items:", error);
      }
    };

    fetchChecklistItems();
  }, [isWorkbookMode, orgSurveyId, organizationSurveyId, widgetId]);

  // ✅ Load widget resources
  useEffect(() => {
    if (isWorkbookMode) return undefined;

    const fetchAttachments = async () => {
      try {
        // Only send org context when we actually have one; avoid `organization_survey_id=undefined`.
        const params = orgSurveyId != null
          ? { organization_survey_id: orgSurveyId }
          : undefined;

        const res = await axios.get(`/widgets/${widgetId}/widget_attachments`, { params });
        setResourceAttachments(res.data);
      } catch (err) {
        console.error("Failed to load widget resources", err);
      }
    };

    fetchAttachments();
  }, [isWorkbookMode, orgSurveyId, organizationSurveyId, widgetId]);

  // ✅ Toggle checklist progress
  const handleToggle = async (itemId, newState) => {
    if (!orgSurveyId) {
      toast({
        title: "Missing school context",
        description: "Cannot save checklist progress outside school results.",
        status: "warning",
      });
      return;
    }

    // optimistic update
    setProgress((prev) => ({ ...prev, [itemId]: newState }));
    setChecklistItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, completed: newState } : item
      )
    );

    try {
      await axios.post(`/widgets/${widgetId}/checklist_progresses`, {
        checklist_progress: {
          organization_survey_id: orgSurveyId,
          checklist_item_id: itemId,
          completed: newState,
        },
      });

      toast({
        title: "Progress saved",
        status: "success",
        duration: 1500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Failed to save progress",
        status: "error",
      });

      // rollback
      setProgress((prev) => ({ ...prev, [itemId]: !newState }));
      setChecklistItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, completed: !newState } : item
        )
      );
    }
  };

  // ✅ Secure resource preview + download (presigned GET + download)
  const openResource = async (attachment) => {
    try {
      setLoadingPreview(true);
      setActiveAttachment(attachment);
      setPreviewUrl(null);
      setDownloadUrl(null);

      const key =
        attachment.key ||
        attachment.upload?.key ||
        extractS3Key(attachment.s3_url || attachment.url);

      if (!key) {
        throw new Error("Missing attachment key");
      }

      const basePayload = { key, upload: { key } };
      const scopedPayload = orgSurveyId != null
        ? { ...basePayload, organization_survey_id: orgSurveyId }
        : basePayload;

      const [previewRes, downloadRes] = await Promise.all([
        axios.post("/uploads/presign_get", scopedPayload),
        axios.post("/uploads/presign_download", scopedPayload),
      ]);

      setPreviewUrl(previewRes.data.url);
      setDownloadUrl(downloadRes.data.url);
    } catch (e) {
      console.error(e);
      toast({
        title: "Failed to load resource",
        status: "error",
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  if (loading) return <Spinner size="xl" />;
  if (widgetError?.status === 403) {
    return (
      <Text color="orange.500">
        {widgetError.message || "You do not have permission to view this widget."}
      </Text>
    );
  }
  if (widgetError) {
    return <Text color="red.500">{widgetError.message || "Failed to load widget"}</Text>;
  }
  if (!widget) return <Text color="red.500">Widget not found</Text>;

  console.log("[Widget] Incoming widget:", widget);

  const normalizedSteps =
    Array.isArray(widget?.widget_steps)
      ? widget.widget_steps
      : Array.isArray(widget?.steps)
      ? widget.steps
      : [];
  const attachments = isWorkbookMode
    ? widget?.attachments || widget?.widget_attachments || resourceAttachments || []
    : resourceAttachments;
  const hasSteps = normalizedSteps.length > 0;
  const hasAttachments = attachments.length > 0;
  const hasOverview = hasMeaningfulContent(widget.overview);
  const hasReflection = hasMeaningfulContent(widget.reflection);
  const hasTabbedContent =
    hasSteps || (!isWorkbookMode && checklistItems.length > 0) || hasAttachments;

  console.log("[Widget] Normalized attachments:", attachments);
  console.log("[Widget] hasAttachments:", hasAttachments);
  console.log("[Widget] Full widget payload:", widget);
  console.log("[Widget] Attachments:", widget?.attachments || widget?.widget_attachments);
  console.log("[Widget] Attachments (final):", attachments);
  console.log("[Widget] Normalized steps:", normalizedSteps);

  if (!hasSteps && !hasAttachments && !hasOverview && !hasReflection) {
    return (
      <Box borderWidth="1px" borderRadius="lg" p={6} boxShadow="sm" bg="white">
        <Heading size="md" mb={2}>
          {widget.title}
        </Heading>
        <Text>This practice is not fully configured yet.</Text>
      </Box>
    );
  }

  return (
    <Box borderWidth="1px" borderRadius="lg" p={6} boxShadow="sm" bg="white">
      <Heading size="md" mb={2}>
        {widget.title}
      </Heading>

      <Box className="tiptap-content" fontSize="md" mb={4}>
        {parse(widget.overview || "")}
      </Box>

      {hasTabbedContent && (
        <Tabs variant="enclosed" colorScheme="teal">
          <TabList>
            {normalizedSteps.map((step) => (
              <Tab key={step.id}>{step.title}</Tab>
            ))}
            {!isWorkbookMode && checklistItems.length > 0 && <Tab>Checklist</Tab>}
            {hasAttachments && <Tab>Resources</Tab>}
          </TabList>

          <TabPanels>
            {/* ✅ STEPS */}
            {normalizedSteps.map((step) => (
              <TabPanel key={step.id}>
                <WidgetStep step={step} />
              </TabPanel>
            ))}

            {/* ✅ CHECKLIST */}
            {!isWorkbookMode && checklistItems.length > 0 && (
              <TabPanel>
                <VStack align="stretch" spacing={6}>
                  {Object.entries(
                    checklistItems.reduce((acc, item) => {
                      const key = item.category || "General";
                      acc[key] = acc[key] || [];
                      acc[key].push(item);
                      return acc;
                    }, {})
                  ).map(([category, items]) => (
                    <Box key={category}>
                      <Heading
                        size="sm"
                        color="teal.600"
                        mb={2}
                        borderBottom="1px solid"
                        borderColor="teal.200"
                        pb={1}
                      >
                        {category}
                      </Heading>

                      <VStack align="stretch" spacing={3}>
                        {items.map((item) => (
                          <Flex
                            key={item.id}
                            align="center"
                            justify="space-between"
                            p={3}
                            borderWidth="1px"
                            borderRadius="md"
                            bg={progress[item.id] ? "teal.50" : "gray.50"}
                            _hover={{
                              bg: progress[item.id] ? "teal.100" : "gray.100",
                            }}
                          >
                            <HStack spacing={3}>
                              <Checkbox
                                colorScheme="teal"
                                isChecked={progress[item.id] ?? item.completed}
                                onChange={() =>
                                  handleToggle(
                                    item.id,
                                    !(progress[item.id] ?? item.completed)
                                  )
                                }
                              />
                              <Text
                                fontWeight="medium"
                                textDecoration={
                                  progress[item.id] ? "line-through" : "none"
                                }
                              >
                                {item.label}
                              </Text>
                            </HStack>
                          </Flex>
                        ))}
                      </VStack>
                    </Box>
                  ))}
                </VStack>
              </TabPanel>
            )}

            {/* ✅ RESOURCES – split layout (list + preview panel) */}
            {hasAttachments && (
              <TabPanel>
                <Flex
                  direction={{ base: "column", md: "row" }}
                  gap={6}
                  align="stretch"
                >
                  {/* LEFT: RESOURCE LIST */}
                  <Box
                    flex={{ base: "none", md: "1" }}
                    borderWidth="1px"
                    borderRadius="md"
                    p={3}
                    bg="gray.50"
                    maxH={{ base: "auto", md: "420px" }}
                    overflowY="auto"
                  >
                    <Heading size="sm" mb={3}>
                      Resources
                    </Heading>
                    <VStack align="stretch" spacing={2}>
                      {attachments.map((a) => {
                        const isActive = activeAttachment?.id === a.id;
                        return (
                          <Flex
                            key={a.id}
                            as="button"
                            onClick={() => openResource(a)}
                            justify="space-between"
                            align="center"
                            p={2}
                            borderWidth="1px"
                            borderRadius="md"
                            bg={isActive ? "teal.50" : "white"}
                            _hover={{
                              bg: isActive ? "teal.100" : "gray.100",
                            }}
                            textAlign="left"
                          >
                            <HStack spacing={3} align="center">
                              {fileIconFor(a.file_type)}
                              <Box>
                                <Text fontWeight="medium" noOfLines={1}>
                                  {a.title}
                                </Text>
                                {a.file_name && (
                                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                    {a.file_name}
                                  </Text>
                                )}
                              </Box>
                            </HStack>
                            <Badge
                              fontSize="0.6rem"
                              colorScheme={
                                a.file_type?.startsWith("image")
                                  ? "purple"
                                  : a.file_type?.startsWith("video")
                                  ? "pink"
                                  : a.file_type === "application/pdf"
                                  ? "red"
                                  : "gray"
                              }
                            >
                              {a.file_type?.split("/")[1] || "file"}
                            </Badge>
                          </Flex>
                        );
                      })}
                    </VStack>
                  </Box>

                  {/* RIGHT: PREVIEW PANEL */}
                  <Box
                    flex={{ base: "none", md: "2" }}
                    borderWidth="1px"
                    borderRadius="md"
                    p={4}
                    bg="gray.50"
                    minH={{ base: "260px", md: "420px" }}
                  >
                    {!activeAttachment && (
                      <Flex
                        h="100%"
                        align="center"
                        justify="center"
                        direction="column"
                        color="gray.500"
                      >
                        <Text fontWeight="medium" mb={1}>
                          Select a resource to preview
                        </Text>
                        <Text fontSize="sm">
                          Choose a file on the left to see details and preview here.
                        </Text>
                      </Flex>
                    )}

                    {activeAttachment && (
                      <>
                        <Flex justify="space-between" align="center" mb={3}>
                          <Box>
                            <Heading size="sm" mb={1}>
                              {activeAttachment.title}
                            </Heading>
                            {activeAttachment.file_name && (
                              <Text fontSize="xs" color="gray.600">
                                {activeAttachment.file_name}
                              </Text>
                            )}
                          </Box>

                          <HStack spacing={2}>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setActiveAttachment(null);
                                setPreviewUrl(null);
                                setDownloadUrl(null);
                              }}
                            >
                              Close
                            </Button>
                            <Button
                              as="a"
                              href={downloadUrl || "#"}
                              target="_blank"
                              rel="noopener noreferrer"
                              leftIcon={<FaDownload />}
                              size="sm"
                              colorScheme="orange"
                              variant="solid"
                              isDisabled={!downloadUrl}
                            >
                              Download
                            </Button>
                          </HStack>
                        </Flex>

                        <Divider mb={3} />

                        {loadingPreview && (
                          <Flex justify="center" align="center" h="100%">
                            <Spinner />
                          </Flex>
                        )}

                        {!loadingPreview && previewUrl && (
                          <Box
                            borderRadius="md"
                            overflow="hidden"
                            bg="white"
                            p={2}
                            maxH={{ base: "400px", md: "500px" }}
                          >
                            {activeAttachment.file_type?.startsWith("image") && (
                              <Image
                                src={previewUrl}
                                alt={activeAttachment.title}
                                maxW="100%"
                                maxH="480px"
                                objectFit="contain"
                                borderRadius="md"
                              />
                            )}

                            {activeAttachment.file_type?.startsWith("video") && (
                              <video
                                controls
                                style={{ width: "100%", maxHeight: "480px" }}
                              >
                                <source
                                  src={previewUrl}
                                  type={activeAttachment.file_type}
                                />
                              </video>
                            )}

                            {activeAttachment.file_type === "application/pdf" && (
                              <Box
                                as="iframe"
                                src={previewUrl}
                                title="PDF Preview"
                                width="100%"
                                height="480px"
                                border="none"
                              />
                            )}

                            {!activeAttachment.file_type?.startsWith("image") &&
                              !activeAttachment.file_type?.startsWith("video") &&
                              activeAttachment.file_type !== "application/pdf" && (
                                <Box>
                                  <Text fontSize="sm" mb={2}>
                                    This file type cannot be previewed here.
                                  </Text>
                                  <Link
                                    href={previewUrl}
                                    isExternal
                                    color="teal.500"
                                    fontSize="sm"
                                  >
                                    Open file in new tab
                                  </Link>
                                </Box>
                              )}
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                </Flex>
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      )}

      {/* ✅ Reflection */}
      {hasReflection && (
        <Box mt={6} p={4} bg="gray.50" borderRadius="md">
          <Heading size="md" mb={2}>
            {widget.reflection_title || DEFAULT_REFLECTION_TITLE}
          </Heading>
          <Box className="tiptap-content">{parse(widget.reflection)}</Box>
        </Box>
      )}
    </Box>
  );
};

export default Widget;
